/* iter55g18 — Bulk-novel Draft dialog.
 *
 * Lets the writer:
 *   1. Analyze the drafted manuscript (Claude infers tone / arc / cast).
 *   2. Kick off a bulk draft of every un-drafted chapter with a chosen
 *      strategy (sequential / sequential-with-pause / all-at-once).
 *   3. Watch progress live (polling every 3 s while a job is active).
 *   4. Cancel or resume a paused job.
 *
 * The dialog is deliberately compact: it lives in the manuscript
 * export/toolbar menu and drives every long-running write from one
 * place. All heavy lifting is on the backend worker
 * (novel_build_jobs.py) — this component is a thin controller.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Play, PauseCircle, StopCircle, ChevronDown,
  BookOpen, Wand2, X, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { novelBuildApi } from '@/lib/api';
import { providerHandle } from '@/lib/providerLabel';   // Feb-2026 · Ask OPi/Claude/GEMi

// The three strategies map 1:1 to backend `mode` values. Copy is
// deliberately blunt so writers don't have to re-read on subsequent
// visits.
const MODES = [
  {
    key:   'sequential',
    label: '(a) Sequential — safest',
    blurb: 'Drafts every un-drafted chapter one at a time. Cancel any time; each finished chapter is saved immediately.',
  },
  {
    key:   'sequential_with_pause',
    label: '(b) Sequential with mid-book review',
    blurb: 'Drafts the first few chapters, then pauses so you can review tone/direction before the rest. Resume when ready.',
    extra: 'pause_after',   // renders a number input
  },
  {
    key:   'all_at_once',
    label: '(c) All-at-once — fire and forget',
    blurb: "Same as sequential, but never pauses. Close the tab and come back — we'll finish the whole novel in the background.",
  },
];

const ACTIVE_STATES  = new Set(['queued', 'running']);
const DONE_STATES    = new Set(['done', 'error', 'cancelled']);
const POLL_INTERVAL  = 3000;

export default function BulkNovelDraftDialog({ open, onClose, project }) {
  // Feb-2026 · provider-aware CTA labels ("Ask OPi/Claude/GEMi").
  const modelId = project?.model_id || '';
  const handle  = modelId.startsWith('gpt-')     ? providerHandle({ provider: 'openai' })
                : modelId.startsWith('gemini-')  ? providerHandle({ provider: 'gemini' })
                : modelId.startsWith('claude-')  ? providerHandle({ provider: 'anthropic' })
                : 'AI';
  const [mode, setMode]                 = useState('sequential');
  const [pauseAfter, setPauseAfter]     = useState(3);
  const [useProfile, setUseProfile]     = useState(true);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [job, setJob]                   = useState(null);
  const [busy, setBusy]                 = useState(false);      // start-click spinner
  const [analyzing, setAnalyzing]       = useState(false);
  const [analysis, setAnalysis]         = useState(null);
  const pollRef                         = useRef(null);

  // On open (and project change) hydrate the latest job for this project.
  useEffect(() => {
    if (!open || !project?.id) return;
    novelBuildApi.status(project.id).then((r) => setJob(r || null)).catch(() => {});
  }, [open, project?.id]);

  // Poll while active.
  useEffect(() => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
    if (!open || !job?.id) return;
    if (!ACTIVE_STATES.has(job.status)) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const fresh = await novelBuildApi.get(job.id);
        setJob(fresh);
        if (DONE_STATES.has(fresh.status)) {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
          if (fresh.status === 'done')       toast.success('Novel build complete!');
          if (fresh.status === 'error')      toast.error(`Build failed: ${fresh.error || 'unknown'}`);
          if (fresh.status === 'cancelled')  toast.info('Build cancelled.');
        }
      } catch { /* ignore transient errors */ }
    }, POLL_INTERVAL);
    return () => {
      if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open, job?.id, job?.status]);

  const analyze = async () => {
    if (!project?.id) return;
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const r = await novelBuildApi.analyze(project.id);
      setAnalysis(r);
      toast.success('Manuscript analysis ready.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analysis failed.');
    } finally {
      setAnalyzing(false);
    }
  };

  const startBuild = async () => {
    if (!project?.id) return;
    setBusy(true);
    try {
      const started = await novelBuildApi.start(project.id, {
        mode, pause_after: pauseAfter, use_voice_profile: useProfile,
      });
      setJob(started);
      toast.success('Build started — you can close this tab.');
    } catch (e) {
      // iter55g21 — Show the upgrade CTA when backend enforces the tier gate.
      if (e?.response?.status === 402) {
        const d = e.response.data?.detail || {};
        toast.info(d.message || 'This feature requires a paid plan.', {
          description: `Upgrade to ${d.target_tier_label || 'Pro Author'} in Settings → Subscription.`,
          duration: 8000,
        });
      } else {
        toast.error(e?.response?.data?.detail || 'Could not start build.');
      }
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (!job?.id) return;
    try {
      const r = await novelBuildApi.cancel(job.id);
      setJob(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed.');
    }
  };
  const doResume = async () => {
    if (!job?.id) return;
    try {
      const r = await novelBuildApi.resume(job.id);
      setJob(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resume failed.');
    }
  };

  const chosen  = useMemo(() => MODES.find((m) => m.key === mode) || MODES[0], [mode]);
  const isActive   = job && ACTIVE_STATES.has(job.status);
  const isPaused   = job?.status === 'paused';
  const isFinished = job && DONE_STATES.has(job.status);
  const percent    = job
    ? Math.min(100, Math.round(((job.completed?.length || 0) / Math.max(1, job.total_chapters)) * 100))
    : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden"
        data-testid="bulk-novel-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            Generate the rest of this novel
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Claude reads every chapter you've already written, extracts a
            voice profile (tone / POV / cadence / cast), then drafts all
            un-drafted chapters back-to-back to your target word count.
          </DialogDescription>
        </DialogHeader>

        {/* Analyze the manuscript */}
        <div className="rounded-md border border-panel-border bg-card p-2" data-testid="bulk-analyze-section">
          <div className="flex items-center gap-2">
            <Wand2 className="h-3.5 w-3.5 text-primary" />
            <span className="text-[12px] font-semibold">Ask {handle} to analyze current manuscript</span>
            <Button
              size="sm"
              variant="secondary"
              className="ml-auto h-6 gap-1 px-2 text-[10.5px]"
              disabled={analyzing || !project?.id}
              onClick={analyze}
              data-testid="bulk-analyze-btn"
            >
              {analyzing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {analyzing ? 'Analyzing…' : 'Run analysis'}
            </Button>
          </div>
          {analysis && (
            <div className="mt-2 space-y-1 text-[10.5px]" data-testid="bulk-analysis-out">
              {analysis.voice_profile && (
                <div>
                  <b>Voice</b> · {[
                    analysis.voice_profile.tone,
                    analysis.voice_profile.pov,
                    analysis.voice_profile.tense,
                    analysis.voice_profile.voice,
                  ].filter(Boolean).join(' · ')}
                </div>
              )}
              {analysis.voice_profile?.setting && (
                <div><b>Setting</b> · {analysis.voice_profile.setting}</div>
              )}
              {(analysis.characters || []).length > 0 && (
                <div>
                  <b>Cast</b> · {analysis.characters.slice(0, 8).map((c) => c.name).join(', ')}
                </div>
              )}
              {analysis.three_act_synopsis?.act_2 && (
                <div className="italic text-muted-foreground">
                  Act 2 (inferred): {analysis.three_act_synopsis.act_2}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mode picker — dropdown */}
        <div className="relative rounded-md border border-panel-border bg-card p-2">
          <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            Draft strategy
          </div>
          <button
            type="button"
            onClick={() => setDropdownOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-sm border border-panel-border bg-background px-2 py-1.5 text-left text-[12px] hover:bg-secondary"
            data-testid="bulk-mode-trigger"
            aria-expanded={dropdownOpen}
          >
            <div>
              <div className="font-semibold">{chosen.label}</div>
              <div className="text-[10.5px] font-normal text-muted-foreground">{chosen.blurb}</div>
            </div>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {dropdownOpen && (
            <ul
              className="absolute left-2 right-2 top-full z-30 mt-1 space-y-0.5 rounded-md border border-panel-border bg-popover p-1 shadow-lg"
              data-testid="bulk-mode-menu"
            >
              {MODES.map((m) => (
                <li key={m.key}>
                  <button
                    type="button"
                    onClick={() => { setMode(m.key); setDropdownOpen(false); }}
                    data-testid={`bulk-mode-${m.key}`}
                    className={
                      'flex w-full flex-col rounded-sm px-2 py-1.5 text-left transition-colors '
                      + (mode === m.key ? 'bg-primary/15 text-primary' : 'hover:bg-secondary')
                    }
                  >
                    <span className="text-[12px] font-semibold">{m.label}</span>
                    <span className="text-[10.5px] text-muted-foreground">{m.blurb}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {mode === 'sequential_with_pause' && (
            <label className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
              Pause after
              <input
                type="number"
                min={1}
                max={25}
                value={pauseAfter}
                onChange={(e) => setPauseAfter(Math.max(1, Number(e.target.value || 1)))}
                data-testid="bulk-pause-after"
                className="h-6 w-14 rounded-sm border border-panel-border bg-background px-1 text-[11px]"
                disabled={isActive}
              />
              chapters
            </label>
          )}
          <label className="mt-2 flex items-center gap-2 text-[10.5px] text-muted-foreground">
            <input
              type="checkbox"
              checked={useProfile}
              onChange={(e) => setUseProfile(e.target.checked)}
              className="accent-primary"
              disabled={isActive}
              data-testid="bulk-use-voice"
            />
            Analyze voice from the drafted chapters before starting
            <span className="italic">(recommended)</span>
          </label>
        </div>

        {/* Progress card */}
        {job && (
          <div className="rounded-md border border-panel-border bg-card p-2" data-testid="bulk-progress">
            <div className="flex items-center gap-2 text-[11.5px]">
              {job.status === 'running' && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
              {job.status === 'queued'  && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              {job.status === 'paused'  && <PauseCircle className="h-3.5 w-3.5 text-amber-500" />}
              {job.status === 'done'    && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
              {job.status === 'error'   && <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />}
              {job.status === 'cancelled' && <X className="h-3.5 w-3.5 text-muted-foreground" />}
              <span className="font-semibold capitalize" data-testid="bulk-progress-status">
                {job.status}
              </span>
              <span className="text-muted-foreground">
                · {(job.completed || []).length} / {job.total_chapters} chapters
                {job.status === 'running' && job.current_chapter
                  ? ` · drafting Ch ${job.current_chapter}`
                  : ''}
              </span>
              <div className="ml-auto flex gap-1">
                {(isActive || isPaused) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 gap-1 px-2 text-[10.5px]"
                    onClick={doCancel}
                    data-testid="bulk-cancel-btn"
                  >
                    <StopCircle className="h-3 w-3" /> Cancel
                  </Button>
                )}
                {isPaused && (
                  <Button
                    size="sm"
                    className="h-6 gap-1 px-2 text-[10.5px]"
                    onClick={doResume}
                    data-testid="bulk-resume-btn"
                  >
                    <Play className="h-3 w-3" /> Resume
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-background">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${percent}%` }}
                data-testid="bulk-progress-bar"
              />
            </div>
            {job.error && (
              <div className="mt-1 rounded-sm bg-rose-500/10 px-2 py-1 text-[10.5px] text-rose-700 dark:text-rose-300">
                {job.error}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
          {!isActive && !isPaused && (
            <Button
              onClick={startBuild}
              disabled={busy || !project?.id}
              data-testid="bulk-start-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
              {isFinished ? 'Run again' : 'Draft the novel'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
